import { FC, useContext, useState } from "react";
import {
  Box,
  Dialog,
  Typography,
  useMediaQuery,
  Grid,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import ProjectPoolHeading from "../Projects/ProjectPoolHeading";
import cross from "../../assets/images/cross.svg";
import useStyles from "../Styles/ProjectModalStyles";
import Textfield from "../FormsUI/Textfield";
import ButtonSubmit from "../FormsUI/Button";
import view from "../../assets/images/view.svg";
import unview from "../../assets/images/unview.svg";
import AuthContext from "../../store/Auth/AuthContext";
import AlertContext from "../../store/Alert/AlertContext";
import graphQLClient from "../../utils/authHeader";
import UPDATE_EMAIL_TAG from "../../utils/mutations/updateProfile/updateEmail";

interface ChangeEmailProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangeEmail: FC<ChangeEmailProps> = ({ isOpen, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { RefreshTokens } = useContext<any>(AuthContext);
  const { setAlert } = useContext<any>(AlertContext);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const INITIAL_FORM_STATE = {
    password: "",
    email: "",
  };

  const FORM_VALIDATION = Yup.object().shape({
    email: Yup.string().required("Email Required").email(),
    password: Yup.string().required("Password Required"),
  });

  const View = () => {
    return <img src={view} className="img-fluid" alt="Gains" />;
  };
  const Unview = () => {
    return <img src={unview} className="img-fluid " alt="Gains" />;
  };

  const isActive = useMediaQuery(theme.breakpoints.down("sm"));
  const [password, setPassword] = useState({
    showPassword: false,
  });

  const handleClickShowPassword = () => {
    setPassword({ ...password, showPassword: !password.showPassword });
  };

  const handleMouseDownPassword = (event: { preventDefault: () => void }) => {
    event.preventDefault();
  };

  const changeEmailSubmit = (
    values: { email: string; password: string },
    options: { setSubmitting: (value: boolean) => void }
  ) => {
    const { setSubmitting } = options;
    main();
    async function main() {
      setSubmitting(true);
      const variables = {
        changeEmailInput: values,
      };
      graphQLClient
        .request(UPDATE_EMAIL_TAG, variables)
        .then((data) => {
          setAlert(data.changeEmail.message, "success");
          setSubmitting(false);
          setIsSubmitted(true);
        })
        .catch((err) => {
          setSubmitting(false);
          if (
            err?.response?.errors[0]?.extensions?.response?.statusCode === 401
          ) {
            RefreshTokens();
          } else {
            if (err?.response?.errors) {
              err?.response?.errors?.map(
                (newerro: {
                  message: any;
                  extensions: {
                    response: {
                      message: string[];
                    };
                  };
                }) => {
                  let message;
                  if (Array.isArray(newerro?.extensions?.response?.message)) {
                    message = newerro?.extensions?.response?.message?.[0];
                  } else {
                    message = newerro?.extensions?.response?.message;
                  }
                  setAlert(message, "warning");
                }
              );
            } else {
              setAlert(
                "Something went wrong while updating password.",
                "error"
              );
            }
          }
        });
    }
  };

  return (
    <>
      <Box component="section">
        <Dialog
          open={isOpen}
          onClose={onClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className={classes.modalpages}
          maxWidth="md"
        >
          <Box
            ml={isActive ? "20px" : 7}
            my={isActive ? "20px" : 7}
            mr={isActive ? "20px" : 3}
          >
            <Box className="relative-box">
              {!isActive && (
                <img
                  src={cross}
                  alt=""
                  className="cross-icon"
                  onClick={onClose}
                />
              )}
              <ProjectPoolHeading title1="Change " title2="Email" />
              <Box component="span">
                <Typography variant="body1" className="fw-normal t-color ">
                  Please note that when changing your email, you maybe ask you
                  to <br />
                  set yourself a secure password.
                </Typography>
              </Box>

              <Box mt={isActive ? 3 : 3} mr={isActive ? 0 : 4}>
                <Formik
                  initialValues={{
                    ...INITIAL_FORM_STATE,
                  }}
                  validationSchema={FORM_VALIDATION}
                  onSubmit={(values, { setSubmitting }) => {
                    changeEmailSubmit(values, { setSubmitting });
                  }}
                >
                  {({ values, isValid, isSubmitting, handleChange }) => (
                    <Form>
                      <Grid container spacing={isActive ? 2 : 3}>
                        <Grid item md={12} xs={12}>
                          <Typography
                            variant="subtitle1"
                            color="secondary"
                            className="fw-normal"
                          >
                            Email*
                          </Typography>
                          <Textfield
                            name="email"
                            type="email"
                            placeholder="Enter email"
                            margin="dense"
                            onChange={(e) => {
                              handleChange(e);
                              setIsSubmitted(false);
                            }}
                          />
                        </Grid>
                        <Grid item md={12} xs={12}>
                          <Typography
                            variant="subtitle1"
                            color="secondary"
                            className="fw-normal"
                          >
                            Password*
                          </Typography>
                          <Textfield
                            name="password"
                            value={values.password}
                            type={password.showPassword ? "text" : "password"}
                            placeholder="Enter New Password"
                            margin="dense"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="start">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                    color="secondary"
                                  >
                                    {password.showPassword ? (
                                      <Unview />
                                    ) : (
                                      <View />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            onChange={(e) => {
                              handleChange(e);
                              setIsSubmitted(false);
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Box className="longbutton" mt={3}>
                        <ButtonSubmit
                          disabled={!isValid || isSubmitting || isSubmitted}
                        >
                          Save Changes {isSubmitting && <CircularProgress />}
                        </ButtonSubmit>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </Box>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </>
  );
};

export default ChangeEmail;
